import { useState, useCallback } from "react";

const CA_MAX_USERS = 5;
const CA_MIN_USERS = 2;
const CA_USER_COLORS = [
  { accent: "var(--green)", dim: "var(--green-dim)", border: "#1a4a28" },
  { accent: "var(--blue)", dim: "var(--blue-dim)", border: "#1a3a5c" },
  { accent: "var(--amber)", dim: "var(--amber-dim)", border: "#4a3600" },
  { accent: "#b57bee", dim: "#1e0a38", border: "#4a2278" },
  { accent: "#f07070", dim: "#2d0f0f", border: "#5a1a1a" },
];

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";

function caNameParts(displayName) {
  if (!displayName) return { first: "", last: "" };
  const parts = displayName.split(",");
  if (parts.length >= 2) return { last: parts[0].trim(), first: parts.slice(1).join(",").trim() };
  const words = displayName.trim().split(" ");
  return words.length >= 2
    ? { first: words.slice(0, -1).join(" "), last: words[words.length - 1] }
    : { first: displayName, last: "" };
}

function caDownloadCSV(rows, users) {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const userCols = users.map((u, i) => u?.displayName || `Employee ${i + 1}`);
  const header = ["Access Profile", "Description", "Source", ...userCols].map(escape).join(",");
  const dataRows = rows.map(r => {
    const userVals = users.map((_, i) => r[`User${i + 1}`] || "");
    return [r.AccessProfile, r.Description, r.Source, ...userVals].map(escape).join(",");
  });
  const csv = [header, ...dataRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `compare-access-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function caBuildCompareRows(userAccessLists, userMeta) {
  const allProfiles = new Set();
  userAccessLists.forEach(list => list.forEach(r => allProfiles.add(r.displayName)));
  return [...allProfiles].map(profile => {
    const row = { AccessProfile: profile };
    let description = "";
    let source = "";
    userAccessLists.forEach((list, idx) => {
      const match = list.find(r => r.displayName === profile);
      row[`User${idx + 1}`] = match ? (match.revocable === "Requested" || match.revocable === true ? "Requested" : "Birthright") : null;
      if (match) {
        if (!description) description = match.description || "";
        if (!source) source = match.sourceName || "";
      }
    });
    row.Description = description;
    row.Source = source;
    userMeta.forEach((u, i) => {
      row[`User${i + 1}Name`] = u?.displayName || "";
    });
    return row;
  });
}

function CaEmployeeLookupPanel({ targetLabel, onSelect, onClose }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(null);

  const handleSearch = async () => {
    if (!firstName.trim() && !lastName.trim()) {
      setError("Enter at least a first or last name.");
      return;
    }
    setLoading(true);
    setError("");
    setResults(null);
    setSelected(null);
    try {
      const res = await fetch(`${API_BASE}/employee-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `API error ${res.status}`);
      setResults(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div className="ca-lookup-panel">
      <div className="ca-lookup-header">
        <span className="ca-lookup-title">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          Search by name → populates <strong style={{ color: "var(--text-primary)" }}>{targetLabel}</strong>
        </span>
        <button className="ca-lookup-close" onClick={onClose}>✕</button>
      </div>
      <div className="ca-lookup-inputs">
        <div className="ca-lookup-field">
          <label className="control-label">First Name</label>
          <input
            autoFocus
            className="cug-input"
            placeholder="e.g. Jane"
            value={firstName}
            onChange={e => {
              setFirstName(e.target.value);
              setError("");
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
        </div>
        <div className="ca-lookup-field">
          <label className="control-label">Last Name</label>
          <input
            className="cug-input"
            placeholder="e.g. Smith"
            value={lastName}
            onChange={e => {
              setLastName(e.target.value);
              setError("");
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
        </div>
        <button
          className="compare-btn"
          onClick={handleSearch}
          disabled={loading || (!firstName.trim() && !lastName.trim())}
          style={{ alignSelf: "flex-end" }}
        >
          {loading ? <><span className="spinner" /> Searching…</> : "Search"}
        </button>
      </div>
      {error && <div className="ca-lookup-error">{error}</div>}
      {results !== null && (
        results.length === 0
          ? <div className="ca-lookup-empty">No employees found. Try different names.</div>
          : (
            <div className="ca-lookup-table-wrap">
              <div className="ca-lookup-thead">
                <div className="ca-lth">Employee ID</div>
                <div className="ca-lth">First Name</div>
                <div className="ca-lth">Last Name</div>
                <div className="ca-lth">Email</div>
              </div>
              <div className="ca-lookup-tbody">
                {results.map((r, i) => (
                  <div
                    key={i}
                    className={`ca-lookup-tr ${selected === r.employeeNumber ? "ca-lookup-tr-selected" : ""}`}
                    onClick={() => {
                      setSelected(r.employeeNumber);
                      onSelect(r.employeeNumber);
                    }}
                  >
                    <div className="ca-ltd"><span className="ca-emp-id-badge">{r.employeeNumber || "—"}</span></div>
                    <div className="ca-ltd">{r.firstName || "—"}</div>
                    <div className="ca-ltd">{r.lastName || "—"}</div>
                    <div className="ca-ltd ca-ltd-muted">{r.email || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )
      )}
    </div>
  );
}

function CaEmpSlot({ index, value, onChange, onRemove, canRemove, disabled, color }) {
  const [showLookup, setShowLookup] = useState(false);
  const label = `Employee ${index + 1}`;
  return (
    <div className="ca-slot-wrap">
      <div className="ca-slot" style={{ "--slot-accent": color.accent, "--slot-dim": color.dim }}>
        <div className="ca-slot-header">
          <span className="ca-slot-label" style={{ color: color.accent }}>
            <span className="ca-slot-dot" style={{ background: color.accent }} />
            {label}
          </span>
          <div className="ca-slot-actions">
            <button
              className={`ca-lookup-toggle ${showLookup ? "ca-lookup-toggle-active" : ""}`}
              onClick={() => setShowLookup(v => !v)}
              disabled={disabled}
              style={showLookup ? { color: color.accent, borderColor: color.accent, background: color.dim } : {}}
              title="Search by name"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              </svg>
              {showLookup ? "Hide" : "Search by name"}
            </button>
            {canRemove && (
              <button className="ca-slot-remove" onClick={onRemove} disabled={disabled} title={`Remove ${label}`}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        <input
          className="cug-input ca-slot-input"
          placeholder={`Enter Employee ID ${index + 1}…`}
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
        />
      </div>
      {showLookup && (
        <div className="ca-slot-popover">
          <CaEmployeeLookupPanel
            targetLabel={label}
            onSelect={(id) => {
              onChange(id);
              setShowLookup(false);
            }}
            onClose={() => setShowLookup(false)}
          />
        </div>
      )}
    </div>
  );
}

function CaGrantBadge({ val }) {
  if (!val) return <span className="ca-cell-missing">—</span>;
  const cls = val === "Requested" ? "ca-grant-requested" : "ca-grant-birthright";
  return <span className={`ca-grant-badge ${cls}`}>{val}</span>;
}

function CaCompareTable({ rows, users, searchText, sourceFilter }) {
  const filtered = rows.filter(r => {
    const matchSource = sourceFilter.length === 0 || sourceFilter.includes(r.Source);
    const q = searchText.toLowerCase();
    if (!q) return matchSource;
    return matchSource && (
      (r.AccessProfile || "").toLowerCase().includes(q) ||
      (r.Description || "").toLowerCase().includes(q) ||
      (r.Source || "").toLowerCase().includes(q) ||
      users.some((_, i) => (r[`User${i + 1}`] || "").toLowerCase().includes(q))
    );
  });
  const sorted = [...filtered].sort((a, b) => (a.Source || "").localeCompare(b.Source || ""));
  const cols = `minmax(180px, 1.2fr) minmax(150px, 1fr) ${users.map(() => "minmax(120px, 1fr)").join(" ")}`;

  return (
    <div className="ca-table-wrap">
      <div className="ca-thead" style={{ gridTemplateColumns: cols }}>
        <div className="ca-th">Access Profile</div>
        <div className="ca-th">Source</div>
        {users.map((u, i) => {
          const { first, last } = caNameParts(u?.displayName);
          return (
            <div
              key={i}
              className="ca-th ca-th-user"
              style={{ color: CA_USER_COLORS[i].accent, borderBottom: `2px solid ${CA_USER_COLORS[i].accent}` }}
              title={u?.displayName}
            >
              <span className="ca-th-user-dot" style={{ background: CA_USER_COLORS[i].accent }} />
              <span className="ca-th-user-name">
                <span className="ca-th-name-first">{first || u?.displayName || `Employee ${i + 1}`}</span>
                {last && <span className="ca-th-name-last">{last}</span>}
              </span>
            </div>
          );
        })}
      </div>
      <div className="ca-tbody">
        {sorted.length === 0
          ? <div className="ca-empty">No results match your current filters.</div>
          : sorted.map((r, i) => (
            <div key={i} className="ca-tr" style={{ gridTemplateColumns: cols }}>
              <div className="ca-td ca-td-profile" title={r.Description || r.AccessProfile || "—"}>{r.AccessProfile || "—"}</div>
              <div className="ca-td"><span className="as-source-badge">{r.Source || "—"}</span></div>
              {users.map((_, ui) => (
                <div key={ui} className="ca-td ca-td-grant"><CaGrantBadge val={r[`User${ui + 1}`]} /></div>
              ))}
            </div>
          ))}
      </div>
      <div className="ca-table-footer">
        Showing <strong style={{ color: "var(--text-primary)" }}>{sorted.length}</strong> of {rows.length} access profiles
      </div>
    </div>
  );
}

export default function CompareAccess() {
  const [slotCount, setSlotCount] = useState(CA_MIN_USERS);
  const [empIds, setEmpIds] = useState(Array(CA_MAX_USERS).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [compareRows, setCompareRows] = useState(null);
  const [userMeta, setUserMeta] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sourceFilter, setSourceFilter] = useState([]);

  const setEmpId = (idx, val) => setEmpIds(prev => {
    const n = [...prev];
    n[idx] = val;
    return n;
  });

  const addSlot = () => setSlotCount(c => Math.min(c + 1, CA_MAX_USERS));
  const removeSlot = (idx) => {
    setEmpIds(prev => {
      const n = [...prev];
      n.splice(idx, 1);
      n.push("");
      return n;
    });
    setSlotCount(c => Math.max(c - 1, CA_MIN_USERS));
  };

  const handleReset = () => {
    setEmpIds(Array(CA_MAX_USERS).fill(""));
    setSlotCount(CA_MIN_USERS);
    setCompareRows(null);
    setUserMeta([]);
    setError("");
    setSearchText("");
    setSourceFilter([]);
  };

  const handleCompare = useCallback(async () => {
    const activeIds = empIds.slice(0, slotCount).map(id => id.trim()).filter(Boolean);
    if (activeIds.length < 2) {
      setError("Please enter at least 2 Employee IDs to compare.");
      return;
    }

    setLoading(true);
    setError("");
    setCompareRows(null);
    setUserMeta([]);
    setSearchText("");
    setSourceFilter([]);

    try {
      const responses = await Promise.all(
        activeIds.map(id =>
          fetch(`${API_BASE}/access-search`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ employeeId: id }),
          }).then(r => r.json())
        )
      );

      const notFound = responses.map((d, i) => (!d.found ? activeIds[i] : null)).filter(Boolean);
      if (notFound.length > 0) {
        setError(`No user found for Employee ID(s): ${notFound.join(", ")}. Please verify and try again.`);
        return;
      }

      const userAccessLists = responses.map(d => (d.results || []).filter(r => r.type === "ACCESS_PROFILE" || !r.type));
      const meta = responses.map(d => d.userInfo || null);
      const rows = caBuildCompareRows(userAccessLists, meta);
      if (rows.length === 0) {
        setError("No access profiles found for any of the entered employees.");
        return;
      }

      setUserMeta(meta);
      setCompareRows(rows);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, [empIds, slotCount]);

  const activeUsers = userMeta.filter(Boolean);
  const sources = compareRows ? [...new Set(compareRows.map(r => r.Source).filter(Boolean))].sort() : [];
  const toggleSource = useCallback(
    (s) => setSourceFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]),
    []
  );

  const handleExport = () => {
    if (!compareRows) return;
    const visible = compareRows.filter(r => {
      const matchSource = sourceFilter.length === 0 || sourceFilter.includes(r.Source);
      const q = searchText.toLowerCase();
      if (!q) return matchSource;
      return matchSource && (
        (r.AccessProfile || "").toLowerCase().includes(q) ||
        (r.Source || "").toLowerCase().includes(q) ||
        activeUsers.some((_, i) => (r[`User${i + 1}`] || "").toLowerCase().includes(q))
      );
    });
    caDownloadCSV(visible, activeUsers);
  };

  return (
    <div className="ca-root">
      <div className="ca-header">
        <div className="ca-header-inner">
          <div className="logo-mark">V</div>
          <div>
            <div className="ca-title">Compare Access</div>
            <div className="ca-subtitle">Compare access profiles across up to {CA_MAX_USERS} employees side-by-side</div>
          </div>
        </div>
      </div>

      <div className="ca-input-panel">
        <div className="ca-slots-grid">
          {Array.from({ length: slotCount }).map((_, i) => (
            <CaEmpSlot
              key={i}
              index={i}
              value={empIds[i]}
              onChange={(val) => setEmpId(i, val)}
              onRemove={() => removeSlot(i)}
              canRemove={slotCount > CA_MIN_USERS}
              disabled={loading}
              color={CA_USER_COLORS[i]}
            />
          ))}
          {slotCount < CA_MAX_USERS && (
            <button className="ca-add-slot" onClick={addSlot} disabled={loading}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Employee {slotCount + 1}
              <span className="ca-add-slot-hint">({CA_MAX_USERS - slotCount} more available)</span>
            </button>
          )}
        </div>
        <div className="ca-input-footer">
          <button
            className="compare-btn ca-compare-btn"
            onClick={handleCompare}
            disabled={loading || empIds.slice(0, slotCount).filter(id => id.trim()).length < 2}
          >
            {loading
              ? <><span className="spinner" /> Comparing…</>
              : <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                  </svg>
                  Compare Access
                </>}
          </button>
          {compareRows !== null && <button className="cug-reset-btn" onClick={handleReset}>Reset</button>}
          <span className="ca-slot-counter">
            {empIds.slice(0, slotCount).filter(id => id.trim()).length} / {slotCount} filled
          </span>
        </div>
      </div>

      {error && (
        <div className="as-error-bar" style={{ margin: "16px 32px 0" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}

      {compareRows === null && !loading && !error && (
        <div className="as-info-panel" style={{ margin: "32px 32px 0" }}>
          <div className="as-info-title">How to use Compare Access</div>
          <div className="as-info-body">
            <p>Enter Employee IDs for 2–{CA_MAX_USERS} employees and click <strong style={{ color: "var(--green)" }}>Compare Access</strong> to see their profiles side-by-side.</p>
            <div className="as-info-features">
              {[
                ["👥", `Compare up to ${CA_MAX_USERS} employees simultaneously`],
                ["🔍", "Search and filter the results table instantly"],
                ["🏷", "Filter by source application"],
                ["📋", "See Requested vs Birthright grant types per user"],
                ["🔎", "Don't know an ID? Use name search on each slot"],
                ["⬇", "Export visible results to CSV"],
              ].map(([icon, text]) => (
                <div key={text} className="as-info-feature"><span>{icon}</span> {text}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {compareRows !== null && (
        <div className="ca-results">
          <div className="ca-user-bar">
            {activeUsers.map((u, i) => (
              <div
                key={i}
                className="ca-user-chip"
                style={{ borderColor: CA_USER_COLORS[i].accent, background: CA_USER_COLORS[i].dim }}
              >
                <span className="ca-user-avatar" style={{ background: CA_USER_COLORS[i].accent, color: "#0b1126" }}>
                  {(u?.displayName || "?").charAt(0).toUpperCase()}
                </span>
                <div className="ca-user-info-block">
                  <div className="ca-user-name" style={{ color: CA_USER_COLORS[i].accent }}>{u?.displayName || "—"}</div>
                  <div className="ca-user-email">{u?.email || "—"}</div>
                </div>
              </div>
            ))}
            <div className="ca-summary-pill">
              {compareRows.length} total profiles · {sources.length} source{sources.length !== 1 ? "s" : ""}
            </div>
          </div>

          <div className="ca-controls">
            <input
              className="search-input"
              placeholder="Search profiles, sources, descriptions…"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            <button className="compare-btn" onClick={handleExport} style={{ padding: "0 16px" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export CSV
            </button>
          </div>

          {sources.length > 1 && (
            <div className="ca-source-filter">
              <span className="ca-filter-label">Source:</span>
              <div className="ca-filter-pills">
                {sources.map(s => (
                  <button key={s} className={`ca-pill ${sourceFilter.includes(s) ? "ca-pill-active" : ""}`} onClick={() => toggleSource(s)}>{s}</button>
                ))}
              </div>
              {sourceFilter.length > 0 && <button className="ca-clear-btn" onClick={() => setSourceFilter([])}>Clear</button>}
            </div>
          )}

          <CaCompareTable
            rows={compareRows}
            users={activeUsers}
            searchText={searchText}
            sourceFilter={sourceFilter}
          />
        </div>
      )}

      <style>{`
        .ca-root { min-height: 100%; background: var(--bg-base); }
        .ca-header { background: var(--bg-surface); border-bottom: 1px solid var(--border); padding: 20px 32px; position: sticky; top: 0; z-index: 10; }
        .ca-header-inner { display: flex; align-items: center; gap: 14px; }
        .ca-title { font-family: var(--font-display); font-size: 18px; font-weight: 700; color: var(--text-primary); }
        .ca-subtitle { font-family: var(--font-body); font-size: 11px; color: var(--text-muted); margin-top: 3px; }
        .ca-input-panel { background: var(--bg-surface); border-bottom: 1px solid var(--border); padding: 24px 32px; }
        .ca-slots-grid { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 20px; align-items: flex-start; }
        .ca-slot-wrap { position: relative; min-width: 260px; flex: 1 1 260px; max-width: 360px; }
        .ca-slot { background: var(--bg-raised); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; transition: border-color 0.2s; width: 100%; }
        .ca-slot:focus-within { border-color: var(--slot-accent, var(--green)); }
        .ca-slot-header { display: flex; align-items: center; justify-content: space-between; }
        .ca-slot-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; display: flex; align-items: center; gap: 6px; }
        .ca-slot-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
        .ca-slot-actions { display: flex; align-items: center; gap: 6px; }
        .ca-lookup-toggle { display: flex; align-items: center; gap: 5px; background: none; border: 1px solid var(--border-light); border-radius: var(--radius-md); color: var(--text-muted); font-size: 11px; font-family: var(--font-body); padding: 4px 10px; cursor: pointer; transition: color 0.15s, border-color 0.15s, background 0.15s; white-space: nowrap; }
        .ca-lookup-toggle:hover:not(:disabled) { color: var(--text-secondary); border-color: var(--text-muted); }
        .ca-lookup-toggle-active { color: var(--green) !important; border-color: var(--green) !important; background: var(--green-dim) !important; }
        .ca-slot-remove { display: flex; align-items: center; justify-content: center; background: none; border: 1px solid transparent; border-radius: var(--radius-sm); color: var(--text-muted); cursor: pointer; padding: 3px 5px; transition: color 0.15s, border-color 0.15s, background 0.15s; }
        .ca-slot-remove:hover:not(:disabled) { color: var(--red); border-color: var(--red); background: var(--red-dim); }
        .ca-slot-remove:disabled { opacity: 0.3; cursor: not-allowed; }
        .ca-slot-input { width: 100% !important; }
        .ca-slot-popover { position: absolute; top: calc(100% + 6px); left: 0; width: 480px; max-width: calc(100vw - 220px); z-index: 200; box-shadow: 0 8px 32px rgba(0,0,0,0.5); border-radius: var(--radius-lg); overflow: hidden; }
        .ca-add-slot { display: flex; align-items: center; gap: 8px; background: var(--bg-raised); border: 1px dashed var(--border-light); border-radius: var(--radius-lg); color: var(--text-muted); font-size: 13px; font-family: var(--font-body); font-weight: 500; padding: 14px 20px; cursor: pointer; transition: color 0.2s, border-color 0.2s, background 0.2s; min-width: 200px; flex: 0 0 auto; align-self: flex-start; }
        .ca-add-slot:hover:not(:disabled) { color: var(--green); border-color: var(--green); background: var(--green-dim); }
        .ca-add-slot:disabled { opacity: 0.3; cursor: not-allowed; }
        .ca-add-slot-hint { font-size: 10px; color: var(--text-muted); font-weight: 400; }
        .ca-input-footer { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .ca-compare-btn { font-size: 14px !important; padding: 0 24px !important; height: 40px !important; }
        .ca-slot-counter { font-size: 11px; color: var(--text-muted); margin-left: auto; }
        .ca-lookup-panel { background: var(--bg-base); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 14px; display: flex; flex-direction: column; gap: 12px; }
        .ca-lookup-header { display: flex; align-items: center; justify-content: space-between; }
        .ca-lookup-title { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-muted); font-style: italic; }
        .ca-lookup-close { background: none; border: none; color: var(--text-muted); cursor: pointer; font-size: 14px; line-height: 1; padding: 2px 6px; border-radius: var(--radius-sm); transition: color 0.15s, background 0.15s; }
        .ca-lookup-close:hover { color: var(--red); background: var(--red-dim); }
        .ca-lookup-inputs { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; }
        .ca-lookup-field { display: flex; flex-direction: column; gap: 6px; }
        .ca-lookup-error { font-size: 12px; color: var(--red); padding: 4px 0; }
        .ca-lookup-empty { font-size: 13px; color: var(--text-muted); padding: 8px 0; }
        .ca-lookup-table-wrap { border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; max-height: 260px; overflow-y: auto; width: 100%; }
        .ca-lookup-thead { display: grid; grid-template-columns: 110px 1fr 1fr 1.4fr; background: var(--bg-raised); border-bottom: 1px solid var(--border); position: sticky; top: 0; width: 100%; }
        .ca-lth { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); padding: 7px 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-lth:not(:last-child) { border-right: 1px solid var(--border); }
        .ca-lookup-tbody { display: flex; flex-direction: column; width: 100%; }
        .ca-lookup-tr { display: grid; grid-template-columns: 110px 1fr 1fr 1.4fr; border-bottom: 1px solid var(--border); cursor: pointer; transition: background 0.1s; width: 100%; }
        .ca-lookup-tr:last-child { border-bottom: none; }
        .ca-lookup-tr:hover { background: var(--bg-hover); }
        .ca-lookup-tr-selected { background: var(--green-dim) !important; }
        .ca-ltd { padding: 9px 10px; font-size: 12px; color: var(--text-secondary); display: flex; align-items: center; overflow: hidden; }
        .ca-ltd:not(:last-child) { border-right: 1px solid var(--border); }
        .ca-ltd-muted { font-size: 11px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-emp-id-badge { font-size: 10px; padding: 2px 7px; border-radius: var(--radius-sm); background: var(--green-dim); color: var(--green); border: 1px solid #1a4a28; font-weight: 600; }
        .ca-results { padding: 24px 32px 64px; display: flex; flex-direction: column; gap: 16px; }
        .ca-user-bar { display: flex; align-items: flex-start; gap: 10px; flex-wrap: wrap; }
        .ca-user-chip { display: flex; align-items: center; gap: 10px; border: 1px solid; border-radius: var(--radius-lg); padding: 10px 14px; min-width: 190px; }
        .ca-user-avatar { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; flex-shrink: 0; }
        .ca-user-info-block { flex: 1; min-width: 0; }
        .ca-user-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-user-email { font-size: 11px; color: var(--text-muted); margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-summary-pill { font-size: 11px; color: var(--text-muted); background: var(--bg-raised); border: 1px solid var(--border-light); border-radius: 999px; padding: 4px 12px; align-self: center; white-space: nowrap; }
        .ca-controls { display: flex; align-items: center; gap: 10px; }
        .ca-table-wrap { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; overflow-x: auto; width: 100%; }
        .ca-thead { display: grid; background: var(--bg-raised); border-bottom: 1px solid var(--border); width: 100%; }
        .ca-th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); padding: 10px 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-th:not(:last-child) { border-right: 1px solid var(--border); }
        .ca-th-user { display: flex; align-items: center; gap: 6px; padding-bottom: 8px; }
        .ca-th-user-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
        .ca-th-user-name { display: flex; flex-direction: column; gap: 1px; min-width: 0; overflow: hidden; }
        .ca-th-name-first { font-size: 11px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-th-name-last { font-size: 10px; font-weight: 400; opacity: 0.7; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-tbody { width: 100%; }
        .ca-tr { display: grid; border-bottom: 1px solid var(--border); transition: background 0.1s; width: 100%; }
        .ca-tr:last-child { border-bottom: none; }
        .ca-tr:hover { background: var(--bg-hover); }
        .ca-td { padding: 10px 14px; font-size: 13px; color: var(--text-secondary); display: flex; align-items: center; }
        .ca-td:not(:last-child) { border-right: 1px solid var(--border); }
        .ca-td-profile { font-weight: 500; color: var(--text-primary); word-break: break-word; align-items: flex-start; padding-top: 12px; }
        .ca-td-grant { justify-content: center; }
        .ca-cell-missing { font-size: 11px; color: var(--text-muted); opacity: 0.4; }
        .ca-grant-badge { font-size: 10px; padding: 3px 9px; border-radius: 999px; font-weight: 500; white-space: nowrap; }
        .ca-grant-requested { background: var(--green-dim); color: var(--green); border: 1px solid #1a4a28; }
        .ca-grant-birthright { background: var(--amber-dim); color: var(--amber); border: 1px solid #4a3600; }
        .ca-table-footer { padding: 10px 14px; font-size: 11px; color: var(--text-muted); border-top: 1px solid var(--border); background: var(--bg-raised); }
        .ca-empty { padding: 40px 24px; text-align: center; font-size: 13px; color: var(--text-muted); }
        .ca-source-filter { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .ca-filter-label { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap; }
        .ca-filter-pills { display: flex; gap: 6px; flex-wrap: wrap; }
        .ca-pill { font-size: 11px; padding: 4px 12px; border-radius: 999px; border: 1px solid var(--border); background: var(--bg-raised); color: var(--text-muted); cursor: pointer; transition: all 0.15s; white-space: nowrap; font-family: var(--font-body); }
        .ca-pill:hover { border-color: var(--border-light); color: var(--text-secondary); background: var(--bg-hover); }
        .ca-pill-active { background: var(--blue-dim) !important; border-color: var(--blue) !important; color: var(--blue) !important; }
        .ca-clear-btn { font-size: 11px; padding: 4px 10px; border-radius: 999px; border: 1px solid var(--border); background: transparent; color: var(--text-muted); cursor: pointer; transition: color 0.15s; font-family: var(--font-body); }
        .ca-clear-btn:hover { color: var(--red); border-color: var(--red); }
      `}</style>
    </div>
  );
}
